import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material/styles';
import { SearchableMultiSelect } from 'components/common/SearchableMultiSelect';
import studioStyles from '../environments/StudioModal.module.scss';

export type PromotionFilterOptions = {
  statuses: string[];
  levels: string[];
  configTypes: string[];
  components?: string[];
};

export type PromotionFilterProps = {
  filterOptions: PromotionFilterOptions;
  filterStatus: string[];
  filterLevel: string[];
  filterConfigType: string[];
  filterComponent?: string[];
  onFilterStatusChange: (value: string[]) => void;
  onFilterLevelChange: (value: string[]) => void;
  onFilterConfigTypeChange: (value: string[]) => void;
  onFilterComponentChange?: (value: string[]) => void;
  onResetAll: () => void;
  onPageReset?: () => void;
  /** Include Component filter (used in ConfigComparePage, not in ConfigCompareReviewPage) */
  includeComponent?: boolean;
  /** Additional content to render in the panel before the filters (e.g. Sorting UI) */
  panelContentBefore?: React.ReactNode;
  /** Button size variant */
  buttonSize?: 'sm' | 'md';
  /** Additional chips to show in the active filters bar (e.g. search chip) */
  topChips?: React.ReactNode;
};

function handleChange(
  onChange: (value: string[]) => void,
  onPageReset?: () => void
) {
  return (value: string[]) => {
    onChange(value);
    onPageReset?.();
  };
}

export function PromotionFilter({
  filterOptions,
  filterStatus,
  filterLevel,
  filterConfigType,
  filterComponent = [],
  onFilterStatusChange,
  onFilterLevelChange,
  onFilterConfigTypeChange,
  onFilterComponentChange,
  onResetAll,
  onPageReset,
  includeComponent = false,
  panelContentBefore,
  buttonSize = 'md',
  topChips,
}: PromotionFilterProps) {
  const theme = useTheme();
  const palette = theme.palette;
  const [filtersPanelOpen, setFiltersPanelOpen] = React.useState(false);

  const activeFilterCount =
    (filterStatus.length ? 1 : 0) +
    (filterLevel.length ? 1 : 0) +
    (includeComponent && filterComponent.length ? 1 : 0) +
    (filterConfigType.length ? 1 : 0);

  const toggleFilterValue = React.useCallback(
    (kind: 'status' | 'level' | 'component' | 'configType', value: string, add: boolean) => {
      const setters = {
        status: onFilterStatusChange,
        level: onFilterLevelChange,
        component: onFilterComponentChange!,
        configType: onFilterConfigTypeChange,
      };
      const getters = {
        status: () => filterStatus,
        level: () => filterLevel,
        component: () => filterComponent,
        configType: () => filterConfigType,
      };
      const arr = getters[kind]();
      const next = add ? (arr.includes(value) ? arr : [...arr, value]) : arr.filter((x) => x !== value);
      setters[kind](next);
      onPageReset?.();
    },
    [
      filterStatus,
      filterLevel,
      filterComponent,
      filterConfigType,
      onFilterStatusChange,
      onFilterLevelChange,
      onFilterComponentChange,
      onFilterConfigTypeChange,
      onPageReset,
    ]
  );

  const hasActiveFilters =
    !!topChips ||
    filterStatus.length > 0 ||
    filterLevel.length > 0 ||
    (includeComponent && filterComponent.length > 0) ||
    filterConfigType.length > 0;

  const buttonClassName = `${studioStyles.button} ${studioStyles.outlineButton}`;
  const buttonStyle: React.CSSProperties =
    buttonSize === 'sm' ? { height: 32, padding: '0 12px', fontSize: 13 } : {};

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <button
          type="button"
          className={buttonClassName}
          onClick={() => setFiltersPanelOpen(true)}
          style={buttonStyle}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FilterListIcon sx={{ fontSize: 18 }} />
            Filters
          </span>
        </button>
        {activeFilterCount > 0 ? (
          <Box
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              minWidth: 18,
              height: 18,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {activeFilterCount}
          </Box>
        ) : null}
      </Box>

      {hasActiveFilters && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2, minHeight: 32, px: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mr: 0.5,
            }}
          >
            Active Filters:
          </Typography>
          {topChips}
          {filterStatus.map((v) => (
            <Chip
              key={`s-${v}`}
              size="small"
              label={`Status: ${v}`}
              onDelete={() => toggleFilterValue('status', v, false)}
              sx={{ height: 28 }}
            />
          ))}
          {filterLevel.map((v) => (
            <Chip
              key={`l-${v}`}
              size="small"
              label={`Level: ${v}`}
              onDelete={() => toggleFilterValue('level', v, false)}
              sx={{ height: 28 }}
            />
          ))}
          {includeComponent &&
            filterComponent.map((v) => (
              <Chip
                key={`c-${v}`}
                size="small"
                label={`Component: ${v}`}
                onDelete={() => toggleFilterValue('component', v, false)}
                sx={{ height: 28 }}
              />
            ))}
          {filterConfigType.map((v) => (
            <Chip
              key={`t-${v}`}
              size="small"
              label={`Type: ${v}`}
              onDelete={() => toggleFilterValue('configType', v, false)}
              sx={{ height: 28 }}
            />
          ))}
          <Typography
            component="button"
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              border: 0,
              background: 'none',
              p: 0,
            }}
            onClick={onResetAll}
          >
            Reset All
          </Typography>
        </Box>
      )}

      {filtersPanelOpen && (
        <>
          <div
            role="presentation"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
              backgroundColor: 'rgba(0,0,0,0.5)',
            }}
            onClick={() => setFiltersPanelOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setFiltersPanelOpen(false)}
          />
          <div
            role="dialog"
            aria-label="Table Settings & Filters"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              maxWidth: 400,
              zIndex: 1301,
              display: 'flex',
              flexDirection: 'column',
              background: palette.background.paper,
              color: palette.text.primary,
              borderLeft: `1px solid ${palette.divider}`,
              boxShadow: theme.shadows[8],
            }}
          >
            <div
              style={{
                padding: 20,
                borderBottom: `1px solid ${palette.divider}`,
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 18 }}>
                <FilterListIcon style={{ color: palette.primary.main, fontSize: 22 }} />
                Table Settings & Filters
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: 14, color: palette.text.secondary }}>
                {panelContentBefore ? 'Configure sorting and multi-select filters for the table.' : 'Configure multi-select filters for the table.'}
              </p>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 20, textAlign: 'left' }}>
              {panelContentBefore}

              {panelContentBefore && (
                <div
                  style={{
                    height: 1,
                    background: palette.divider,
                    margin: '16px 0',
                  }}
                />
              )}

              <label
                style={{
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: palette.text.secondary,
                  marginBottom: 12,
                  textAlign: 'left',
                }}
              >
                Filters
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SearchableMultiSelect
                  label="Status"
                  options={filterOptions.statuses}
                  selected={filterStatus}
                  onChange={handleChange(onFilterStatusChange, onPageReset)}
                />
                <SearchableMultiSelect
                  label="Level"
                  options={filterOptions.levels}
                  selected={filterLevel}
                  onChange={handleChange(onFilterLevelChange, onPageReset)}
                  emptyOptionLabel="—"
                />
                {includeComponent && filterOptions.components && (
                  <SearchableMultiSelect
                    label="Components"
                    options={filterOptions.components}
                    selected={filterComponent}
                    onChange={handleChange(onFilterComponentChange!, onPageReset)}
                  />
                )}
                <SearchableMultiSelect
                  label="Config Type"
                  options={filterOptions.configTypes}
                  selected={filterConfigType}
                  onChange={handleChange(onFilterConfigTypeChange, onPageReset)}
                />
              </div>
            </div>

            <div
              style={{
                padding: 16,
                borderTop: `1px solid ${palette.divider}`,
                background: palette.action.hover,
                display: 'flex',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={onResetAll}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  background: 'transparent',
                  color: palette.text.primary,
                  border: `1px solid ${palette.divider}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Reset All
              </button>
              <button
                type="button"
                onClick={() => setFiltersPanelOpen(false)}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  background: palette.primary.main,
                  color: palette.primary.contrastText,
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
